import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWeb3 } from '../context/Web3Context';
import { 
  Send, Users, Coins, CheckCircle, AlertTriangle, 
  ExternalLink, Copy, RefreshCw, ArrowLeft, History, Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ethers } from 'ethers';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BETH_TOKEN = "0x6822824c5349bf005e353ec5dda3fa34595ea811";

const ERC20_ABI = [
  "function transfer(address to, uint256 amount) returns (bool)",
  "function balanceOf(address owner) view returns (uint256)"
];

const TokenDistribution = () => {
  const { token } = useAuth();
  const { 
    account, 
    bethBalance,
    connectWallet, 
    isConnecting,
    refreshBalances
  } = useWeb3();

  const [recipients, setRecipients] = useState([{ address: '', amount: '' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [distributions, setDistributions] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (token) {
      fetchDistributions();
    }
  }, [token]);

  const fetchDistributions = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(`${API}/token-distribution`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDistributions(data.distributions || []);
      }
    } catch (error) {
      console.error('Error fetching distributions:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatNumber = (num) => {
    const n = parseFloat(num);
    if (n >= 1e12) return (n / 1e12).toFixed(2) + 'T';
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B';
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K';
    return n.toLocaleString();
  };

  const addRecipient = () => {
    setRecipients([...recipients, { address: '', amount: '' }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const validateAddress = (address) => {
    try {
      return ethers.utils.isAddress(address);
    } catch {
      return false;
    }
  };

  const handleDistribute = async () => {
    if (!account) {
      toast.error('Please connect your wallet first');
      return;
    }

    // Validate all recipients
    for (let i = 0; i < recipients.length; i++) {
      const r = recipients[i];
      if (!r.address || !r.amount) {
        toast.error(`Recipient ${i + 1}: Address and amount required`);
        return;
      }
      if (!validateAddress(r.address)) {
        toast.error(`Recipient ${i + 1}: Invalid Ethereum address`);
        return;
      }
      if (parseFloat(r.amount) <= 0) {
        toast.error(`Recipient ${i + 1}: Amount must be greater than 0`);
        return;
      }
    }

    const totalAmount = recipients.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0);
    if (totalAmount > bethBalance) {
      toast.error('Insufficient BETH balance');
      return;
    }

    setIsLoading(true);

    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const bethContract = new ethers.Contract(BETH_TOKEN, ERC20_ABI, signer);

      for (const recipient of recipients) {
        try {
          // Record distribution first
          await fetch(`${API}/token-distribution/record`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              recipient_wallet: recipient.address,
              amount: parseFloat(recipient.amount),
              purpose: 'manual_distribution',
              notes: `Distribution from ${account}`
            })
          });

          const amountWei = ethers.utils.parseUnits(recipient.amount, 18);
          
          toast.info(`Sending ${formatNumber(recipient.amount)} BETH to ${recipient.address.slice(0, 8)}...`);
          const tx = await bethContract.transfer(recipient.address, amountWei);
          
          toast.info('Transaction submitted. Waiting for confirmation...');
          const receipt = await tx.wait();
          
          // Confirm distribution with tx hash
          await fetch(`${API}/token-distribution/${receipt.transactionHash}/confirm?tx_hash=${receipt.transactionHash}`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}` }
          });

          toast.success(`Sent ${formatNumber(recipient.amount)} BETH to ${recipient.address.slice(0, 8)}...`);
          
        } catch (error) {
          console.error('Distribution error:', error);
          if (error.code === 'ACTION_REJECTED') {
            toast.error('Transaction cancelled');
          } else {
            toast.error(`Failed: ${error.message}`);
          }
        }
      }

      // Refresh data
      refreshBalances();
      fetchDistributions();
      setRecipients([{ address: '', amount: '' }]);
      
    } catch (error) {
      console.error('Distribution error:', error);
      toast.error('Distribution failed');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied!');
  };

  const totalToDistribute = recipients.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);

  return (
    <div className="min-h-screen bg-[#0a1628]" data-testid="token-distribution-page">
      {/* Header */}
      <header className="border-b border-[#1a2d4a] bg-[#0d1d35]">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/treasury">
                <Button variant="ghost" size="sm" className="text-gray-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Treasury
                </Button>
              </Link>
              <div className="h-6 w-px bg-[#1a2d4a]" />
              <div>
                <h1 className="text-xl font-bold text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-[#b8860b]" />
                  Token Distribution
                </h1>
                <p className="text-xs text-gray-500">Distribute BETH tokens to investors and partners</p>
              </div>
            </div>
            {account ? (
              <div className="text-right">
                <p className="text-xs text-gray-400">Treasury Balance</p>
                <p className="text-xl font-bold text-white">{formatNumber(bethBalance)} BETH</p>
              </div>
            ) : (
              <Button onClick={connectWallet} disabled={isConnecting} className="bg-[#b8860b] hover:bg-[#9a7209]">
                {isConnecting ? 'Connecting...' : 'Connect Wallet'}
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {!account ? (
          <Card className="bg-[#0d1d35] border-[#1a2d4a] text-center py-12">
            <CardContent>
              <Shield className="w-16 h-16 text-[#b8860b] mx-auto mb-4" />
              <h2 className="text-xl text-white mb-2">Connect Treasury Wallet</h2>
              <p className="text-gray-400 mb-4">Connect the treasury wallet to distribute BETH tokens</p>
              <Button onClick={connectWallet} className="bg-[#b8860b] hover:bg-[#9a7209]">
                Connect Wallet
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Distribution Form */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#b8860b]" />
                  Recipients
                </CardTitle>
                <CardDescription>Add wallet addresses and amounts to distribute</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recipients.map((recipient, index) => (
                    <div key={index} className="flex gap-4 items-start">
                      <div className="flex-1">
                        <label className="text-xs text-gray-500 mb-1 block">Wallet Address</label>
                        <Input
                          placeholder="0x..."
                          value={recipient.address}
                          onChange={(e) => updateRecipient(index, 'address', e.target.value)}
                          className="bg-[#1a2d4a] border-[#2a3d5a] text-white font-mono text-sm"
                        />
                        {recipient.address && !validateAddress(recipient.address) && (
                          <p className="text-xs text-red-400 mt-1">Invalid address</p>
                        )}
                      </div>
                      <div className="w-48">
                        <label className="text-xs text-gray-500 mb-1 block">Amount (BETH)</label>
                        <Input
                          type="number"
                          placeholder="0"
                          value={recipient.amount}
                          onChange={(e) => updateRecipient(index, 'amount', e.target.value)}
                          className="bg-[#1a2d4a] border-[#2a3d5a] text-white"
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRecipient(index)}
                        disabled={recipients.length === 1}
                        className="mt-6 text-red-400 hover:text-red-300"
                      >
                        ✕
                      </Button>
                    </div>
                  ))}

                  <Button 
                    variant="outline" 
                    onClick={addRecipient} 
                    className="w-full border-[#1a2d4a] text-gray-400"
                  >
                    + Add Another Recipient
                  </Button>

                  <div className="border-t border-[#1a2d4a] pt-4 mt-4">
                    <div className="flex justify-between text-sm mb-4">
                      <span className="text-gray-400">Total to Distribute:</span>
                      <span className="text-white font-bold">{formatNumber(totalToDistribute)} BETH</span>
                    </div>
                    
                    {totalToDistribute > bethBalance && (
                      <div className="flex items-center gap-2 text-yellow-400 text-sm mb-4 p-3 bg-yellow-900/20 rounded-lg">
                        <AlertTriangle className="w-4 h-4" />
                        Insufficient balance. Available: {formatNumber(bethBalance)} BETH
                      </div>
                    )}

                    <Button
                      className="w-full bg-[#b8860b] hover:bg-[#9a7209]"
                      onClick={handleDistribute}
                      disabled={isLoading || totalToDistribute === 0 || totalToDistribute > bethBalance}
                    >
                      {isLoading ? 'Processing...' : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Distribute {formatNumber(totalToDistribute)} BETH
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Quick Presets */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <CardTitle className="text-white text-lg">Quick Amount Presets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1000000, 10000000, 100000000, 500000000].map(amt => (
                    <Button
                      key={amt}
                      variant="outline"
                      className="border-[#1a2d4a] text-gray-300 hover:bg-[#1a2d4a]"
                      onClick={() => {
                        const updated = [...recipients];
                        updated[0].amount = amt.toString();
                        setRecipients(updated);
                      }}
                    >
                      {formatNumber(amt)} BETH
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Value Lock - Quick Transfers to Contracts */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a] border-[#b8860b]/50">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#b8860b]" />
                  Value Lock - Transfer to Contracts
                </CardTitle>
                <CardDescription>
                  Quick transfer BETH to value lock contracts (Collateral Vault, Treasury, Uniswap Pool)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  {/* Collateral Vault */}
                  <div className="p-4 bg-[#1a2d4a] rounded-lg border border-[#2a3d5a]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Collateral Vault</p>
                        <p className="text-xs text-gray-500">Credit facility backing</p>
                      </div>
                    </div>
                    <p className="text-xs font-mono text-gray-400 mb-3 truncate">0xe86c...d300c</p>
                    <Button
                      size="sm"
                      className="w-full bg-blue-600 hover:bg-blue-700"
                      onClick={() => {
                        const updated = [...recipients];
                        updated[0].address = '0xe86c1f0fd87165cbde68714e6f4cb152e7e5707d';
                        updated[0].amount = '50000000000';
                        setRecipients(updated);
                        toast.success('Collateral Vault selected - 50B BETH');
                      }}
                    >
                      Select (50B BETH)
                    </Button>
                  </div>

                  {/* Treasury Manager */}
                  <div className="p-4 bg-[#1a2d4a] rounded-lg border border-[#2a3d5a]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                        <Coins className="w-4 h-4 text-green-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Treasury Manager</p>
                        <p className="text-xs text-gray-500">Platform reserves</p>
                      </div>
                    </div>
                    <p className="text-xs font-mono text-gray-400 mb-3 truncate">0xc21c...300c</p>
                    <Button
                      size="sm"
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        const updated = [...recipients];
                        updated[0].address = '0xc21cd1629a88fe99dbf4cf5d202c04bd493cbf2c';
                        updated[0].amount = '30000000000';
                        setRecipients(updated);
                        toast.success('Treasury Manager selected - 30B BETH');
                      }}
                    >
                      Select (30B BETH)
                    </Button>
                  </div>

                  {/* Uniswap Pool */}
                  <div className="p-4 bg-[#1a2d4a] rounded-lg border border-[#2a3d5a]">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                        <svg className="w-4 h-4 text-pink-400" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">Uniswap Pool</p>
                        <p className="text-xs text-gray-500">Trading liquidity</p>
                      </div>
                    </div>
                    <p className="text-xs font-mono text-gray-400 mb-3 truncate">0x8F01...19CF</p>
                    <Button
                      size="sm"
                      className="w-full bg-pink-600 hover:bg-pink-700"
                      onClick={() => {
                        const updated = [...recipients];
                        updated[0].address = '0x8F01D20feaA323049976f35865F43D3C457F19CF';
                        updated[0].amount = '20000000000';
                        setRecipients(updated);
                        toast.success('Uniswap Pool selected - 20B BETH');
                      }}
                    >
                      Select (20B BETH)
                    </Button>
                  </div>
                </div>

                <div className="p-3 bg-[#b8860b]/10 border border-[#b8860b]/30 rounded-lg">
                  <p className="text-xs text-[#b8860b]">
                    <strong>Instructions:</strong> Click a contract button above to pre-fill the recipient form, 
                    then click "Distribute" to execute the transfer. You'll need to confirm the transaction in MetaMask.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Distribution History */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <History className="w-5 h-5 text-purple-400" />
                    Distribution History
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={fetchDistributions} disabled={loadingHistory}>
                    <RefreshCw className={`w-4 h-4 ${loadingHistory ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {distributions.length === 0 ? (
                  <p className="text-center text-gray-500 py-6">No distributions recorded yet</p>
                ) : (
                  <div className="space-y-3">
                    {distributions.slice(0, 15).map((dist) => (
                      <div 
                        key={dist.id}
                        className={`p-4 rounded-lg ${
                          dist.status === 'completed' 
                            ? 'bg-green-900/20 border border-green-500/30' 
                            : 'bg-yellow-900/20 border border-yellow-500/30'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {dist.status === 'completed' ? (
                              <CheckCircle className="w-5 h-5 text-green-400" />
                            ) : (
                              <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            )}
                            <div>
                              <p className="text-white font-mono text-sm">
                                {dist.recipient_wallet?.slice(0, 12)}...{dist.recipient_wallet?.slice(-8)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {new Date(dist.created_at).toLocaleDateString()} • {dist.purpose}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-white font-bold">{formatNumber(dist.amount)} BETH</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              dist.status === 'completed' 
                                ? 'bg-green-900/50 text-green-400' 
                                : 'bg-yellow-900/50 text-yellow-400'
                            }`}>
                              {dist.status}
                            </span>
                          </div>
                        </div>
                        {dist.tx_hash && (
                          <a
                            href={`https://etherscan.io/tx/${dist.tx_hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#b8860b] hover:underline flex items-center gap-1 mt-2"
                          >
                            View on Etherscan <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contract Info */}
            <Card className="bg-[#0d1d35] border-[#1a2d4a]">
              <CardContent className="py-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <p className="text-gray-400 text-sm">BETH Token Contract</p>
                    <p className="text-white font-mono text-sm">{BETH_TOKEN}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => copyToClipboard(BETH_TOKEN)}>
                      <Copy className="w-4 h-4 mr-2" /> Copy
                    </Button>
                    <a href={`https://etherscan.io/token/${BETH_TOKEN}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm">
                        <ExternalLink className="w-4 h-4 mr-2" /> Etherscan
                      </Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
};

export default TokenDistribution;
